import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Modal,
  Stack,
  Text,
  Button,
  Alert,
  Group,
  Badge,
  Skeleton,
  Table,
} from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import moment from "moment";
import { useResponsive } from "../../context/ResponsiveContext";
import {
  GiftCardLedgerEntry,
  GiftCardLedgerEntryType,
} from "../../types/giftCards";
import {
  PackageReasonCategory,
  PACKAGE_REASON_CATEGORIES,
} from "../../types/packages";
import { formatCurrency } from "../../utils/cashDrawerUtils";
import { parseServerDateTime } from "../../utils/localDateUtils";

const ENTRY_TYPE_LABELS: Record<GiftCardLedgerEntryType, string> = {
  purchase: "Purchase",
  grant: "Grant",
  adjust: "Adjust",
  redeem: "Redeem",
  void: "Void",
  breakage: "Breakage",
  claim: "Claim",
};

const REASON_LABELS: Record<string, string> = Object.fromEntries(
  PACKAGE_REASON_CATEGORIES.map((r) => [r.value, r.label]),
) as Record<PackageReasonCategory, string>;

const entryTypeColor = (type: GiftCardLedgerEntryType): string => {
  if (type === "void") return "red";
  if (type === "purchase" || type === "grant") return "var(--brand-teal)";
  if (type === "redeem") return "orange";
  return "gray";
};

/** Human label for a reason code, falling back to the raw value or a dash. */
const reasonLabel = (value: string | null): string =>
  value ? (REASON_LABELS[value] ?? value) : "—";

/** Format an audit timestamp in the viewer's local zone (server sends UTC). */
function formatAuditDateTime(value: string | null): string {
  if (!value) return "—";
  const parsed = parseServerDateTime(value);
  return parsed ? moment(parsed).format("MMM D, YYYY h:mm A") : "—";
}

/** Read-only ledger table, newest-first. Money via `formatCurrency`. */
function GiftCardLedgerTable({ entries }: { entries: GiftCardLedgerEntry[] }) {
  const sortedEntries = [...entries].sort(
    (a, b) =>
      (parseServerDateTime(b.created_dt)?.getTime() ?? 0) -
      (parseServerDateTime(a.created_dt)?.getTime() ?? 0),
  );
  if (entries.length === 0) {
    return (
      <Text size="sm" c="dimmed" py="xs">
        No history recorded for this gift card.
      </Text>
    );
  }
  return (
    <Table.ScrollContainer minWidth={680}>
      <Table verticalSpacing="xs" fz="sm" striped withRowBorders={false}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Date</Table.Th>
            <Table.Th>Type</Table.Th>
            <Table.Th ta="right">Change</Table.Th>
            <Table.Th ta="right">Balance</Table.Th>
            <Table.Th>Reason</Table.Th>
            <Table.Th>Note</Table.Th>
            <Table.Th>Staff</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {sortedEntries.map((entry) => (
            <Table.Tr key={entry.id}>
              <Table.Td>{formatAuditDateTime(entry.created_dt)}</Table.Td>
              <Table.Td>
                <Badge
                  size="sm"
                  variant="light"
                  color={entryTypeColor(entry.entry_type)}
                >
                  {ENTRY_TYPE_LABELS[entry.entry_type] ?? entry.entry_type}
                </Badge>
              </Table.Td>
              <Table.Td ta="right">
                <Text
                  size="sm"
                  fw={500}
                  c={
                    entry.delta_amount != null && entry.delta_amount < 0
                      ? "red"
                      : "var(--brand-teal)"
                  }
                >
                  {formatCurrency(entry.delta_amount, { showSign: true })}
                </Text>
              </Table.Td>
              <Table.Td ta="right">
                {formatCurrency(entry.balance_after)}
              </Table.Td>
              <Table.Td>{reasonLabel(entry.reason_category)}</Table.Td>
              <Table.Td>{entry.note || "—"}</Table.Td>
              <Table.Td>
                {entry.created_by_name ?? entry.created_by ?? "—"}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}

interface GiftCardHistoryModalProps {
  opened: boolean;
  onClose: () => void;
  salonId: string;
  giftCardId: string;
  /** The card code, shown in the modal title. */
  code: string;
  getLedger: (
    salonId: string,
    giftCardId: string,
  ) => Promise<GiftCardLedgerEntry[]>;
  /** Bumped after any successful action so an open modal reloads its history. */
  historyToken: number;
}

/**
 * Read-only modal showing a single gift card's ledger history — the
 * money-balance analog of `PackageHistoryModal`. Fetches lazily when opened and
 * refetches when `historyToken` changes while open (so a mutation elsewhere is
 * reflected).
 */
export default function GiftCardHistoryModal({
  opened,
  onClose,
  salonId,
  giftCardId,
  code,
  getLedger,
  historyToken,
}: GiftCardHistoryModalProps) {
  const { isMobile } = useResponsive();
  const [entries, setEntries] = useState<GiftCardLedgerEntry[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const openedRef = useRef(false);

  const loadHistory = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const list = await getLedger(salonId, giftCardId);
      setEntries(list);
    } catch (err) {
      console.error("Error fetching gift card ledger:", err);
      setError("Failed to load history.");
    } finally {
      setIsLoading(false);
    }
  }, [getLedger, salonId, giftCardId]);

  // Fetch on open; refetch on a later historyToken bump while still open.
  useEffect(() => {
    if (opened && !openedRef.current) {
      openedRef.current = true;
      loadHistory();
    } else if (!opened) {
      openedRef.current = false;
    }
  }, [opened, loadHistory]);

  useEffect(() => {
    if (opened && openedRef.current && historyToken > 0) {
      loadHistory();
    }
  }, [historyToken, opened, loadHistory]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={600}>{`Gift card history - ${code}`}</Text>}
      size="xl"
      fullScreen={isMobile}
      centered
    >
      <Stack gap="md">
        {isLoading ? (
          <Stack gap="xs">
            <Skeleton height={28} radius="sm" />
            <Skeleton height={28} radius="sm" />
            <Skeleton height={28} radius="sm" />
          </Stack>
        ) : error ? (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
          >
            <Group justify="space-between" align="center">
              <Text size="sm">{error}</Text>
              <Button
                size="compact-xs"
                variant="light"
                color="red"
                onClick={loadHistory}
              >
                Retry
              </Button>
            </Group>
          </Alert>
        ) : entries ? (
          <GiftCardLedgerTable entries={entries} />
        ) : null}
      </Stack>
    </Modal>
  );
}
